import { DragEvent, useState } from "react"
import { NavLink } from "react-router-dom"
import { logout } from "./ApiInterface"
import { useAccounts } from "./AccountsContext"
import { accountPath, accountToCategorizeCount } from "./Helpers"
import AccountIcon from "./components/AccountIcon"

const Icon = ({ children }: { children: JSX.Element | JSX.Element[] }) =>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        { children }
    </svg>

const Menu = () => {
    const { accounts, sortAccounts } = useAccounts()
    // Glisser-déposer : le compte déplacé, et où il ira (index d'insertion, matérialisé par une
    // ligne en pointillé). Pas de ligne là où le compte ne bougerait pas.
    const [draggedId, setDraggedId] = useState(null as number | null)
    const [dropIndex, setDropIndex] = useState(null as number | null)

    const draggedIndex = accounts.findIndex((account) => account.id === draggedId)
    const moves = dropIndex !== null && draggedIndex >= 0 && dropIndex !== draggedIndex && dropIndex !== draggedIndex + 1

    const onDragStart = (event: DragEvent, account: Account) => {
        event.dataTransfer.effectAllowed = "move"
        event.dataTransfer.setData("text/plain", account.name)
        setDraggedId(account.id)
    }

    // Moitié haute du compte survolé : avant lui ; moitié basse : après.
    const onDragOver = (event: DragEvent, index: number) => {
        if (draggedId === null) return
        event.preventDefault()
        event.dataTransfer.dropEffect = "move"
        const rect = event.currentTarget.getBoundingClientRect()
        setDropIndex(event.clientY < rect.top + rect.height / 2 ? index : index + 1)
    }

    // Fin du glisser, déposé ou annulé (Échap, lâché hors du menu : dropEffect « none »).
    const onDragEnd = (event: DragEvent) => {
        if (event.dataTransfer.dropEffect !== "none" && moves && dropIndex !== null) {
            const ids = accounts.map((account) => account.id).filter((id) => id !== draggedId)
            ids.splice(dropIndex > draggedIndex ? dropIndex - 1 : dropIndex, 0, draggedId!)
            sortAccounts(ids)
        }
        setDraggedId(null)
        setDropIndex(null)
    }

    return <aside className="menu tl-card">
        <div className="brandline">
            <img className="logo-tile" alt="" src="/logo192.png"/>
            <span className="wordmark">Tirelire</span>
        </div>
        <nav>
            <NavLink to="/stats">
                <Icon><path d="M4 20V10M10 20V4M16 20v-7M22 20H2"/></Icon>
                Stats
            </NavLink>
            <NavLink to="/budgets">
                <Icon><circle cx="12" cy="12" r="9"/><path d="M12 3v9l6 6"/></Icon>
                Budgets
            </NavLink>
            <NavLink to="/bank_connections">
                <Icon><path d="M3 10h18M5 10v8M10 10v8M14 10v8M19 10v8M3 20h18M12 3l9 5H3z"/></Icon>
                Banque
            </NavLink>
        </nav>
        { accounts.length > 0 && <>
            <div className="menu-group">Comptes</div>
            {/* Lâché entre deux comptes : le dépôt compte aussi. */}
            <nav className="accounts-nav" onDragOver={(event) => { if (draggedId !== null) event.preventDefault() }}>
                { accounts.map((account, index) => {
                    const toCategorizeCount = accountToCategorizeCount(account)
                    return <NavLink
                        key={account.id}
                        to={accountPath(account)}
                        className={({ isActive }) => [
                            "account-link",
                            isActive && "active",
                            account.id === draggedId && "dragged",
                            moves && dropIndex === index && "drop-before",
                            moves && dropIndex === accounts.length && index === accounts.length - 1 && "drop-after"
                        ].filter(Boolean).join(" ")}
                        title={account.name}
                        draggable
                        onDragStart={(event) => onDragStart(event, account)}
                        onDragOver={(event) => onDragOver(event, index)}
                        onDrop={(event) => event.preventDefault()}
                        onDragEnd={onDragEnd}
                    >
                        <AccountIcon account={account}/>
                        <span className="account-name">{ account.name }</span>
                        { toCategorizeCount > 0 && <span className="to-categorize" title={`${toCategorizeCount} à catégoriser`}>{ toCategorizeCount }</span> }
                    </NavLink>
                }) }
            </nav>
        </> }
        <div className="spacer"/>
        <button className="logout-button" onClick={logout}>
            <Icon><path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H3"/></Icon>
            Se déconnecter
        </button>
    </aside>
}

export default Menu
